import { useBreakpoints } from 'hooks/useBreakpoints';
import { useThirdNavChildren } from 'hooks/useThirdNavChildren';
import { useGetAutoImportsQuery } from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import React, { useMemo, useState, useContext, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AutoImporterView, AutoImportQueryResult, ThirdNavRightButtons } from './AutoImporters.view';
import { BoxNotification } from 'components/containers/BoxNotification';
import classes from './AutoImporters.module.scss';
import { usePauseSettings } from 'hooks/usePauseSettings';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type CreateEditImporterModalType = {
  isOpen: boolean;
  autoImporter?: AutoImportQueryResult;
};
export type ModalState = {
  title: string | undefined;
  id: string | undefined;
  isOpen: boolean;
  importer?: AutoImportQueryResult;
};

export const AutoImportersController = () => {
  const [createEditImporterModalState, setCreateEditImporterModalState] = useState<CreateEditImporterModalType>({
    isOpen: false,
    autoImporter: undefined
  });
  const [deleteModalState, setDeleteModalState] = useState<ModalState>({
    title: undefined,
    id: undefined,
    isOpen: false
  });
  const [pauseModalState, setPauseModalState] = useState<ModalState>({
    title: undefined,
    id: undefined,
    isOpen: false
  });
  const [selectedImporterEdit, setSelectedImporterEdit] = useState<undefined | AutoImportQueryResult>();
  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);
  const { data, loading } = useGetAutoImportsQuery({ variables: { workspaceId: selectedWorkspaceId } });
  const [autoImporters, setAutoImporters] = useState<AutoImportQueryResult[]>([]);
  const breakpoint = useBreakpoints();
  const navigate = useNavigate();
  const { autoImportersPaused } = usePauseSettings();
  const { canAddNewAutoImport } = useOrganizationPlan();

  const shouldNotShowPage = breakpoint === 'medium' || breakpoint === 'small';

  const openDeleteModal = (title?: string, id?: string) => setDeleteModalState({ isOpen: true, title, id });
  const closeDeleteModal = () => setDeleteModalState({ isOpen: false, title: undefined, id: undefined });

  const closePauseModal = () => setPauseModalState({ isOpen: false, title: undefined, id: undefined });
  const openPauseModal = useCallback(
    (autoImport?: AutoImportQueryResult) =>
      setPauseModalState({
        isOpen: true,
        title: undefined,
        id: undefined,
        importer: autoImport
      }),
    [setPauseModalState]
  );

  useEffect(() => {
    if (data && !loading) {
      setAutoImporters(data.workspace.autoImports);
    }
  }, [data, loading]);

  const topNavChildren = useMemo(
    () => ({
      center: autoImportersPaused ? (
        <>
          <BoxNotification customClassNames={classes.BoxContainer} type={'error'}>
            <span>
              No new Posts will be imported while <Link to={'/settings/pause'}>RSS Auto-Importers are paused</Link>.
            </span>
          </BoxNotification>
        </>
      ) : undefined,
      right: (
        <ThirdNavRightButtons
          onCreateAutoImporter={() => {
            if (canAddNewAutoImport()) {
              navigate('/discover/find', { state: { context: 'selectRSS' } });
            } else {
              setLimitReachedModalState({
                isOpen: true,
                type: 'autoRSS'
              });
            }
          }}
          openDeleteModal={openDeleteModal}
          openPauseModal={openPauseModal}
          autoImporters={autoImporters}
          isImportersPaused={autoImportersPaused}
        />
      )
    }),
    [navigate, autoImporters, openPauseModal, autoImportersPaused, canAddNewAutoImport, setLimitReachedModalState]
  );
  useThirdNavChildren(shouldNotShowPage ? undefined : topNavChildren);
  const props = {
    deleteModalState,
    pauseModalState,
    setAutoImporters,
    setDeleteModalState,
    selectedImporterEdit,
    setSelectedImporterEdit,
    openDeleteModal,
    closeDeleteModal,
    openPauseModal,
    closePauseModal,
    autoImporters,
    profileCategories: data?.workspace.categories ?? [],
    socialProfiles: data?.workspace.socialProfiles ?? [],
    loading,
    shouldNotShowPage,
    createEditImporterModalState,
    setCreateEditImporterModalState
  };
  return <AutoImporterView {...props}></AutoImporterView>;
};
